/**
 * The keyboard map. Shortcuts *are* the workflow here — a mouse-only path is a failed design
 * for a tool whose success metric is letters boxed per minute — so the map is a data table
 * rather than a pile of `if (event.key === ...)` in a component. That way the help overlay and
 * the handler cannot drift apart, and the matcher is unit-testable without a DOM.
 */

import type { ReadingFlow } from './draft';

export type Action =
  | 'draw-mode'
  | 'play-letter'
  | 'next-letter'
  | 'prev-letter'
  | 'toggle-uncertain'
  | 'toggle-restored'
  | 'cycle-chirality'
  | 'delete-box'
  | 'mark-absent'
  | 'insert-unexpected'
  | 'relabel-selected'
  | 'class-picker'
  | 'complete-advance'
  | 'skip-image'
  | 'not-pictured'
  | 'prev-record'
  | 'next-record'
  | 'toggle-record-nav'
  | 'save-now'
  | 'undo'
  | 'redo'
  | 'select-next-box'
  | 'select-prev-box'
  | 'deselect'
  | 'rotate-view-ccw'
  | 'rotate-view-cw'
  | 'rotate-view-ccw-coarse'
  | 'rotate-view-cw-coarse'
  | 'rotate-view-reset'
  | 'straighten'
  | 'rotate-box-ccw'
  | 'rotate-box-cw'
  | 'rotate-box-ccw-coarse'
  | 'rotate-box-cw-coarse'
  | 'rotate-box-reset'
  | 'zoom-in'
  | 'zoom-out'
  | 'zoom-fit'
  | 'help';

export interface Binding {
  action: Action;
  /** Lower-cased `KeyboardEvent.key` values that trigger it. */
  keys: string[];
  ctrl?: boolean;
  shift?: boolean;
  /** What the help overlay shows. */
  display: string;
  label: string;
  group: 'Boxing' | 'Letters' | 'Image' | 'View' | 'Session';
}

export const BINDINGS: Binding[] = [
  {
    action: 'draw-mode',
    keys: ['d'],
    display: 'D',
    label: 'Draw mode on/off (drag on the image draws a box; otherwise drag pans)',
    group: 'Boxing',
  },
  {
    action: 'delete-box',
    keys: ['delete', 'backspace'],
    display: 'Del',
    label: 'Delete the selected box',
    group: 'Boxing',
  },
  {
    action: 'select-next-box',
    keys: ['tab'],
    display: 'Tab',
    label: 'Select the next box',
    group: 'Boxing',
  },
  {
    action: 'select-prev-box',
    keys: ['tab'],
    shift: true,
    display: 'Shift+Tab',
    label: 'Select the previous box',
    group: 'Boxing',
  },
  {
    action: 'deselect',
    keys: ['escape'],
    display: 'Esc',
    label: 'Deselect / cancel the drag in progress',
    group: 'Boxing',
  },
  {
    action: 'next-letter',
    keys: ['arrowright', 'arrowdown', ' '],
    display: '→ / ↓ / Space',
    label:
      "Move the cursor to the next expected letter. The arrows follow the record's reading direction — on a right-to-left record ← advances, on a bottom-to-top one ↑ does. Space always advances.",
    group: 'Letters',
  },
  {
    action: 'prev-letter',
    keys: ['arrowleft', 'arrowup'],
    display: '← / ↑',
    label: "Move the cursor to the previous expected letter (the arrows follow the record)",
    group: 'Letters',
  },
  {
    action: 'play-letter',
    keys: ['p'],
    display: 'P',
    label:
      "Hear the letter under the cursor. A synthesized consonant, framed by two support vowels so a stop is audible at all — those vowels are not part of the letter and are not in the text. Silent for an illegible or unknown chip, which are not sounds.",
    group: 'Letters',
  },
  {
    action: 'toggle-uncertain',
    keys: ['u'],
    display: 'U',
    label: 'Toggle uncertain on the selected box (a flag, never a class)',
    group: 'Letters',
  },
  {
    action: 'toggle-restored',
    keys: ['r'],
    display: 'R',
    label: 'Toggle restored on the selected box',
    group: 'Letters',
  },
  // Not a toggle: `chirality` has five values, and one of them means "nobody has looked".
  // The step order is null -> reference -> mirrored -> indeterminate -> null. On a letter form
  // that reads the same either way — about a quarter of this corpus's letter tokens — and on
  // `illegible`/`unknown`, which have no reference form at all, it declines and says why
  // rather than cycling through answers that do not exist. The picker still offers all five.
  {
    action: 'cycle-chirality',
    keys: ['m'],
    display: 'M',
    label: "Step the selected box's handedness: reference / mirrored / indeterminate / not assessed",
    group: 'Letters',
  },
  {
    action: 'mark-absent',
    keys: ['n'],
    display: 'N',
    label: 'Mark the current expected letter as not present on the stone',
    group: 'Letters',
  },
  {
    action: 'insert-unexpected',
    keys: ['i'],
    display: 'I',
    label: 'Arm insertion: the next box is a letter the transliteration does not contain',
    group: 'Letters',
  },
  {
    action: 'relabel-selected',
    keys: ['l'],
    display: 'L',
    label:
      'Relabel the selected box onto the chip under the cursor (clicking a chip only moves the cursor — a class change is never implicit)',
    group: 'Letters',
  },
  {
    action: 'class-picker',
    keys: ['c'],
    display: 'C',
    label:
      'Open the class picker for the selected box — the only way to label a glyph whose letter is nowhere in this transliteration',
    group: 'Letters',
  },
  {
    action: 'complete-advance',
    keys: ['enter'],
    display: 'Enter',
    label: 'Mark the image complete and move to the next one',
    group: 'Image',
  },
  {
    action: 'skip-image',
    keys: ['s'],
    shift: true,
    display: 'Shift+S',
    label: 'Skip this image and move to the next one',
    group: 'Image',
  },
  // Shift-modified like `skip-image`, and for the same reason: both end an image, and neither
  // belongs one keystroke away from the letter keys. `X` because it is nowhere near `S` —
  // these two are the pair most easily confused, and adjacent keys would make the slip silent,
  // since both advance and both look like progress.
  {
    action: 'not-pictured',
    keys: ['x'],
    shift: true,
    display: 'Shift+X',
    label: 'This photograph does not show this inscription — record that and move on',
    group: 'Image',
  },
  // `[` / `]`, and not the obvious alternatives. Arrows are the letter cursor, Tab/Shift+Tab
  // are the box selection, Enter is complete-and-advance and Shift+S is skip — every key that
  // ordinarily means "move through a list" is already the workflow. `[` and `]` are unshifted,
  // adjacent, conventional for previous/next asset in image tools, and nowhere near the
  // destructive keys (Del/Backspace) or the one that finishes an image.
  //
  // These step **positionally**, so they can reach a record that is already finished — the
  // usual reason to press "previous" is to fix a box. Enter's advance keeps its own rule and
  // skips finished work; both walk the same list in the same order (`lib/nav.ts`).
  {
    action: 'prev-record',
    keys: ['['],
    display: '[',
    label:
      'Previous image of this inscription, or the previous inscription in the work-queue list',
    group: 'Image',
  },
  {
    action: 'next-record',
    keys: [']'],
    display: ']',
    label: 'Next image of this inscription, or the next inscription in the work-queue list',
    group: 'Image',
  },
  {
    action: 'toggle-record-nav',
    keys: ['\\'],
    display: '\\',
    label: 'Show or hide the record list beside the canvas (the choice is remembered)',
    group: 'View',
  },
  {
    action: 'save-now',
    keys: ['s'],
    ctrl: true,
    display: 'Ctrl+S',
    label: 'Save now (autosave runs anyway)',
    group: 'Session',
  },
  {
    action: 'undo',
    keys: ['z'],
    ctrl: true,
    display: 'Ctrl+Z',
    label: 'Undo',
    group: 'Session',
  },
  {
    action: 'redo',
    keys: ['z'],
    ctrl: true,
    shift: true,
    display: 'Ctrl+Shift+Z',
    label: 'Redo',
    group: 'Session',
  },
  // Punctuation turns the view; letters turn the selected box. The split is the mnemonic, and
  // it also sidesteps `shiftIsRedundant`: for a printable non-alphanumeric character the shift
  // state carries no information, so `,` and `<` must be bound as distinct `event.key` values
  // rather than as one key with a shift flag. The letter bindings below have real shift
  // semantics and work the ordinary way.
  {
    action: 'rotate-view-ccw',
    keys: [','],
    display: ',',
    label: 'Turn the photograph 1° anticlockwise',
    group: 'View',
  },
  {
    action: 'rotate-view-cw',
    keys: ['.'],
    display: '.',
    label: 'Turn the photograph 1° clockwise',
    group: 'View',
  },
  {
    action: 'rotate-view-ccw-coarse',
    keys: ['<'],
    display: '<',
    label: 'Turn the photograph 15° anticlockwise',
    group: 'View',
  },
  {
    action: 'rotate-view-cw-coarse',
    keys: ['>'],
    display: '>',
    label: 'Turn the photograph 15° clockwise',
    group: 'View',
  },
  {
    action: 'rotate-view-reset',
    keys: [';'],
    display: ';',
    label: 'Put the photograph back the way the camera saw it',
    group: 'View',
  },
  {
    action: 'straighten',
    keys: ['t'],
    display: 'T',
    label: 'Straighten: click two points along the baseline and the view levels it',
    group: 'View',
  },
  {
    action: 'rotate-box-ccw',
    keys: ['q'],
    display: 'Q',
    label: 'Turn the selected box 1° anticlockwise',
    group: 'Boxing',
  },
  {
    action: 'rotate-box-cw',
    keys: ['e'],
    display: 'E',
    label: 'Turn the selected box 1° clockwise',
    group: 'Boxing',
  },
  {
    action: 'rotate-box-ccw-coarse',
    keys: ['q'],
    shift: true,
    display: 'Shift+Q',
    label: 'Turn the selected box 15° anticlockwise',
    group: 'Boxing',
  },
  {
    action: 'rotate-box-cw-coarse',
    keys: ['e'],
    shift: true,
    display: 'Shift+E',
    label: 'Turn the selected box 15° clockwise',
    group: 'Boxing',
  },
  {
    action: 'rotate-box-reset',
    keys: ['w'],
    display: 'W',
    label: "Square the selected box up with the photograph as you are viewing it",
    group: 'Boxing',
  },
  { action: 'zoom-in', keys: ['+', '='], display: '+', label: 'Zoom in', group: 'View' },
  { action: 'zoom-out', keys: ['-', '_'], display: '-', label: 'Zoom out', group: 'View' },
  { action: 'zoom-fit', keys: ['0'], display: '0', label: 'Fit the image to the panel', group: 'View' },
  { action: 'help', keys: ['?', '/'], display: '?', label: 'Show or hide this list', group: 'View' },
];

export interface KeyEventLike {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}

/**
 * True when the shift state carries no information beyond the key itself.
 *
 * `?` **is** `Shift+/` and `+` **is** `Shift+=` on a US layout, so a binding on `?` that also
 * required `shiftKey === false` could never fire — the help overlay and the zoom-in button
 * both advertised keys that did nothing. For a single printable character that is neither a
 * letter nor a digit, `KeyboardEvent.key` has already applied the shift, so the modifier is
 * ignored in both directions.
 *
 * Letters and digits are excluded because there the shift state is real and load-bearing:
 * `Shift+S` (skip) must not fire on a plain `s`. Space is excluded for the same reason.
 */
export function shiftIsRedundant(key: string): boolean {
  return key.length === 1 && key !== ' ' && !/[a-z0-9]/.test(key);
}

/**
 * Resolve a keyboard event to an action, or null.
 *
 * More specific bindings win: `Ctrl+Shift+Z` must not be swallowed by `Ctrl+Z`, and
 * `Shift+Tab` must not be swallowed by `Tab`. That is done by scoring rather than by relying
 * on the order of the table, so adding a binding cannot silently shadow an existing one.
 */
export function matchBinding(event: KeyEventLike): Binding | null {
  const key = event.key.toLowerCase();
  const ctrl = Boolean(event.ctrlKey || event.metaKey);
  const shift = Boolean(event.shiftKey);
  const ignoreShift = shiftIsRedundant(key);
  let best: Binding | null = null;
  let bestScore = -1;
  for (const binding of BINDINGS) {
    if (!binding.keys.includes(key)) continue;
    if (Boolean(binding.ctrl) !== ctrl) continue;
    if (!ignoreShift && Boolean(binding.shift) !== shift) continue;
    const score = (binding.ctrl ? 1 : 0) + (binding.shift ? 1 : 0);
    if (score > bestScore) {
      best = binding;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Whether an arrow key points the opposite way to the sequence, for a record's flow.
 *
 * The pair **on the writing axis** flips when the writing runs backwards along it. The other
 * pair does not: it points where the strip *wraps*, and a wrapped strip always continues down
 * (a new row) and to the right (a new column), whichever way the writing itself runs.
 *
 * So the horizontal pair flips on `rtl` alone and the vertical pair on `btt` alone:
 *
 * | | `→` | `←` | `↓` | `↑` |
 * | --- | --- | --- | --- | --- |
 * | `ltr` / `ttb` / unset | next | prev | next | prev |
 * | `rtl` | **prev** | **next** | next | prev |
 * | `btt` | next | prev | **prev** | **next** |
 *
 * Keyed on the **key**, never on the action: Space is bound to `next-letter` alongside `→` and
 * must keep advancing on every record, having no direction of its own to follow.
 */
function arrowIsReversed(key: string, flow: ReadingFlow): boolean {
  if (key === 'arrowleft' || key === 'arrowright') return !flow.vertical && flow.reversed;
  if (key === 'arrowup' || key === 'arrowdown') return flow.vertical && flow.reversed;
  return false;
}

/**
 * Resolve a keyboard event to an action, or null.
 *
 * `flow` orients the arrow keys to the record being read. Omitting it is the left-to-right
 * reading — which is what the home page's shortcut list wants, having no record behind it.
 */
export function matchAction(event: KeyEventLike, flow?: ReadingFlow): Action | null {
  const action = matchBinding(event)?.action ?? null;
  if (!flow) return action;
  if (action !== 'next-letter' && action !== 'prev-letter') return action;
  if (!arrowIsReversed(event.key.toLowerCase(), flow)) return action;
  return action === 'next-letter' ? 'prev-letter' : 'next-letter';
}

/**
 * The bits of an element the keyboard router needs. Duck-typed rather than `instanceof
 * HTMLElement` so both predicates below are unit-testable without a DOM — the workspace's
 * keyboard is the workflow, and "is it safe to swallow this key" is not something to verify
 * only by hand.
 */
export interface TargetLike {
  tagName?: unknown;
  isContentEditable?: unknown;
  getAttribute?: (name: string) => string | null;
}

function asElement(target: unknown): TargetLike | null {
  if (!target || typeof target !== 'object') return null;
  const candidate = target as TargetLike;
  return typeof candidate.tagName === 'string' ? candidate : null;
}

function tagOf(element: TargetLike): string {
  return String(element.tagName).toLowerCase();
}

function attr(element: TargetLike, name: string): string | null {
  return typeof element.getAttribute === 'function' ? element.getAttribute(name) : null;
}

/**
 * True when the event came from somewhere the annotator is typing, so the shortcut must not
 * fire. Without this, typing a note containing "d" toggles draw mode mid-sentence.
 */
export function isTypingTarget(target: unknown): boolean {
  const element = asElement(target);
  if (!element) return false;
  const tag = tagOf(element);
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    element.isContentEditable === true ||
    attr(element, 'role') === 'textbox'
  );
}

/**
 * True when focus is on a control the browser is entitled to operate itself.
 *
 * The window handler used to `preventDefault()` everything it matched, so with focus on a
 * button or a link `Enter` completed the image instead of activating the control, `Space`
 * stepped the letter cursor, and `Tab` moved the *box* selection rather than focus. The
 * workspace was not keyboard-navigable while it was mounted, which is the opposite of the
 * intent behind making the shortcuts the workflow.
 *
 * So: while a control has focus, the browser wins. Pointer activation blurs the control again
 * (see `blurOnPointerActivation`), so clicking a chip or a toolbar button hands the keyboard
 * straight back and the fast path is unaffected.
 */
export function isInteractiveTarget(target: unknown): boolean {
  const element = asElement(target);
  if (!element) return false;
  if (isTypingTarget(element)) return true;
  const tag = tagOf(element);
  if (tag === 'button' || tag === 'summary' || tag === 'option') return true;
  if (tag === 'a' && attr(element, 'href') !== null) return true;
  const role = attr(element, 'role');
  if (role && ['button', 'link', 'checkbox', 'radio', 'switch', 'option', 'tab'].includes(role)) {
    return true;
  }
  const tabIndex = attr(element, 'tabindex');
  return tabIndex !== null && tabIndex !== '-1';
}

/**
 * Blur a control that was activated with a pointer, returning the keyboard to the workspace.
 *
 * `event.detail === 0` for a click synthesised by `Enter`/`Space` on a focused control, so a
 * keyboard user keeps focus where they put it and only a mouse click gives it back.
 */
export function blurOnPointerActivation(event: { detail?: number; currentTarget?: unknown }): void {
  if ((event.detail ?? 0) <= 0) return;
  const target = event.currentTarget as { blur?: () => void } | undefined;
  if (target && typeof target.blur === 'function') target.blur();
}

export function bindingsByGroup(): Array<[Binding['group'], Binding[]]> {
  const groups: Binding['group'][] = ['Boxing', 'Letters', 'Image', 'View', 'Session'];
  return groups.map((group) => [group, BINDINGS.filter((binding) => binding.group === group)]);
}
